<template>
  <div
    :class="bem('overlay', { active: isActive })"
    @mouseenter="handleClose"
    @click="handleClose"
  />
  <div
    ref="ProductCalloutControls"
    :class="bem({ isActive })"
  >
    <div
      :class="bem('item-list')"
      :style="`--items: ${props.products.length}`"
    >
      <button
        v-for="(product, index) in props.products"
        :key="index"
        :class="
          bem('item', { '--is-active': product.sku === activeProduct.sku })
        "
        :style="`--index: ${index}`"
        @click="handleActiveProductChange(product, index + 1)"
      >
        <div :class="bem('image-clip')">
          <div :class="bem('item-image')">
            <StaticThreeSixtyFrame
              :key="`static-image-360-${14}`"
              :customised-product="product"
              :frame-number="14"
              :show-secondary="false"
              :show-background="true"
            />
          </div>
        </div>
        <div :class="bem('text-clip')">
          <h4>
            <span>{{ product.frameChoice.name }} with </span>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span v-html="product.lensChoice.nameWithoutLensTech" />
          </h4>
        </div>
        <span
          :class="
            bem('indicator', { '--is-active': index === activeProductIndex })
          "
        />
      </button>
    </div>
    <div
      :class="bem('active-item')"
      @mouseenter="handleMouseEnterOpen"
      @click="handleOpen"
    >
      <div :class="bem('image-clip')">
        <div :class="bem('active-item-image')">
          <StaticThreeSixtyFrame
            :key="`static-image-360-${1}`"
            :customised-product="activeProduct"
            :frame-number="1"
            :show-secondary="false"
            :show-background="true"
          />
        </div>
      </div>
      <div :class="bem('item-content')">
        <div :class="bem('text-clip')">
          <h4>
            <span>{{ activeProduct.frameChoice.name }} with </span>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span v-html="activeProduct.lensChoice.nameWithoutLensTech" />
          </h4>
        </div>
        <div :class="bem('indicator-container')">
          <span
            v-for="(product, index) in props.products"
            :key="index"
            :class="
              bem('indicator', {
                '--is-active': product.sku === activeProduct.sku,
              })
            "
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import type CustomisedProduct from '~/models/CustomisedProduct'

interface ProductCalloutControlProps {
  products: Array<typeof CustomisedProduct>
  activeProduct: typeof CustomisedProduct
}

const props = defineProps<ProductCalloutControlProps>()

const emits = defineEmits(['update-active-product'])

const bem = useBEM('ProductCalloutControls')
const { $track } = useNuxtApp()
const { isMdBreakpointOrAbove } = useBreakpoint()

const isActive = ref(false)

function handleOpen() {
  isActive.value = true
}

function handleMouseEnterOpen() {
  if (!isMdBreakpointOrAbove.value) return

  isActive.value = true
}

function handleClose() {
  isActive.value = false
}

async function handleActiveProductChange(product, controlIndex) {
  if (product.sku !== props.activeProduct.sku)
    emits('update-active-product', product.sku)

  isActive.value = false

  await $track.global.linkClicked(
    {
      interactionElement: product.sku,
      interactionCategory: 'Product Callout',
      position: controlIndex,
    },
    'Clicked Product Callout Control'
  )
}

const activeProductIndex = computed(() => {
  return props.products.findIndex(
    (product) => product.sku === props.activeProduct.sku
  )
})
</script>

<style lang="postcss">
.ProductCalloutControls {
  --transition: cubic-bezier(0.215, 0.61, 0.355, 1);
  @apply sticky bottom-5 w-auto text-white pointer-events-none;

  &::before {
    @apply content-[''] absolute inset-0 z-[0];
    border-radius: 8px;
    background: var(--overlayBg, rgba(43, 43, 43, 0.72));
    backdrop-filter: blur(10px);
    clip-path: inset(calc(100% - var(--controls-height)) 40px 0 round 8px);
    transition: clip-path 0.24s var(--transition);
  }

  h4 {
    margin: 0;
  }
}

.ProductCalloutControls__overlay {
  @apply absolute w-full h-full top-0 left-0 pointer-events-none;

  &--active {
    @apply pointer-events-auto;
  }
}

.ProductCalloutControls--is-active {
  .ProductCalloutControls__item-list {
    @apply pointer-events-auto;
  }
}

.ProductCalloutControls__active-item {
  @apply absolute bottom-0 left-[40px] pointer-events-auto
  flex items-center justify-start p-1 pr-4 gap-4 justify-self-center;

  width: 280px;
  height: var(--controls-height);
}

.ProductCalloutControls__active-item-image {
  @apply w-[88px] aspect-square p-1 rounded-[6px];
  background: linear-gradient(180deg, #fff 0%, #f2f2f5 100%);
  transform: translateY(0%);
  transition: transform 0.24s var(--transition);
}

.ProductCalloutControls__item-list {
  @apply relative bottom-0 flex flex-col
    max-h-[70vh] overflow-y-auto scrollbar-none
    pointer-events-none;
}

.ProductCalloutControls__item {
  @apply flex items-center justify-start p-1 pr-16
  gap-4 text-left;

  width: 360px;

  h4 {
    @apply text-gray-500;
    transform: translateY(101%);
    transition: color 0.16s linear;
  }

  .ProductCalloutControls__indicator {
    @apply absolute right-4;
    opacity: 0;
    transform: translateX(4px);
  }
}

.ProductCalloutControls__image-clip {
  clip-path: inset(0 0 0 0 round 6px);
}

.ProductCalloutControls__text-clip {
  @apply font-bold text-base;

  clip-path: inset(0 0 0 0);

  h4 {
    transition: transform 0.16s var(--transition);
  }
}

.ProductCalloutControls__item-image {
  @apply w-[104px] aspect-square p-1 opacity-[0.6];
  border-radius: 6px;
  background: linear-gradient(180deg, #fff 0%, #f2f2f5 100%);
  transform: translateY(101%);
  transition: transform 0.08s var(--transition);
}

.ProductCalloutControls__indicator {
  @apply block w-1 h-1 rounded-full bg-gray-400;
  transition: all 0.16s var(--transition);

  &--is-active {
    @apply bg-white;
  }
}

.ProductCalloutControls__indicator-container {
  @apply flex gap-1 mt-4 pointer-events-auto;
  clip-path: inset(0 0 0 0);
}

.ProductCalloutControls__item--is-active {
  .ProductCalloutControls__indicator {
    @apply bg-white;
  }

  .ProductCalloutControls__item-image {
    @apply opacity-[1];
  }

  h4 {
    @apply text-white;
  }
}

.ProductCalloutControls--is-active {
  @apply pointer-events-auto;

  &::before {
    clip-path: inset(0 0 0 0 round 8px);
    transition-duration: 0.4s;
    transition-delay: 0.08s;
  }

  .ProductCalloutControls__active-item {
    @apply pointer-events-none;

    h4 {
      transform: translateY(-101%);
    }
    &-image {
      transform: translateY(-101%);
    }

    .ProductCalloutControls__indicator {
      transform: translateY(-101%);
    }
  }

  .ProductCalloutControls__item {
    --delay: calc(0.24s + (var(--items) * 0.04s) - (var(--index) * 0.04s));

    .ProductCalloutControls__item-image,
    h4 {
      transform: translateY(0%);
      transition-delay: var(--delay);
      transition-duration: 0.24s;
    }

    .ProductCalloutControls__indicator {
      transform: translateX(0px);
      opacity: 1;
      transition-delay: var(--delay);
    }
  }
}

.ProductCalloutSection--has-button {
  .ProductCalloutControls {
    @apply -translate-y-[56px] transition-transform;
    
    &--is-active {
      @apply -translate-y-0;
    }
  }
}
</style>
